import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { Box, Typography, Divider, Link } from '@mui/material';
import ProfileAvatar from 'components/ProfileAvatar';
import { buildQuarterGoalsReportPath } from 'utils/goalsReportDeepLink';
import { renderMentions } from 'utils/mentionHelpers';

const RANK_EMOJI = { 1: '🥇', 2: '🥈', 3: '🥉' };

const formatConsistency = (score) => `${Number(score || 0).toFixed(1)}%`;

const StatPill = ({ label, value }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.75,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'action.hover',
      minWidth: 0,
      flex: '1 1 120px',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const LeaderboardRow = ({ entries, valueKey, formatValue, showStreakIcon = false }) => {
  if (!entries?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data for this section
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.7,
        overflowX: 'auto',
        overflowY: 'visible',
        pt: 0.5,
        pb: 0.5,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {entries.map((entry) => {
        const rank = entry.rank ?? entry.streakRank;
        const value = entry[valueKey];
        const rankEmoji = RANK_EMOJI[rank] || null;
        return (
          <Box
            key={entry.userId}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 88,
              flexShrink: 0,
            }}
          >
            <ProfileAvatar
              user={entry.user}
              size={56}
              rank={rank}
              showRankBadge
              showEntryRing={false}
            />
            <Typography variant="caption" sx={{ mt: 0.75, textAlign: 'center', maxWidth: 88 }}>
              {rankEmoji ? `${rankEmoji} ` : ''}
              {entry.user?.displayName || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {showStreakIcon && (
                <WhatshotIcon sx={{ fontSize: 14, color: 'orange' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {formatValue(value)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const TopPerformerLine = ({ label, performer, formatValue }) => {
  if (!performer?.user?.displayName) {
    return null;
  }
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {performer.user.displayName}
      </Box>
      {' '}
      · {label}: {formatValue(performer.value)}
    </Typography>
  );
};

const QuarterGoalsReportActivity = ({ payload }) => {
  if (!payload) {
    return (
      <Typography variant="body2" color="text.secondary">
        Quarter goals report
      </Typography>
    );
  }

  const {
    quarterLabel,
    leaderboard = [],
    topStreaks = [],
    topPerformers = {},
    clubStats,
    perfectQuarters = [],
    personalBests = [],
    booksFinished = { total: 0, items: [] },
    shoutoutText,
  } = payload;

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {quarterLabel} Goals Report
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Final quarter standings before the leaderboard resets.
      </Typography>

      {shoutoutText && (
        <Typography variant="body2" sx={{ mb: 1.25, fontWeight: 500 }}>
          {renderMentions(shoutoutText)}
        </Typography>
      )}

      {clubStats && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
            <StatPill label="Avg consistency" value={formatConsistency(clubStats.averageConsistency)} />
            <StatPill label="Check-ins" value={String(clubStats.totalCheckIns ?? 0)} />
            <StatPill label="Active habit goals" value={String(clubStats.activeHabitGoals ?? 0)} />
            <StatPill
              label="Participation"
              value={`${Number(clubStats.participationRate || 0).toFixed(0)}%`}
            />
          </Box>
          {clubStats.participatingMemberCount != null && clubStats.memberCount != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              {clubStats.participatingMemberCount} of {clubStats.memberCount} members logged at least one check-in
            </Typography>
          )}
        </>
      )}

      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Habit consistency
      </Typography>
      <LeaderboardRow
        entries={leaderboard}
        valueKey="consistencyScore"
        formatValue={formatConsistency}
      />

      <Divider sx={{ my: 1.25 }} />

      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Top daily streaks
      </Typography>
      <LeaderboardRow
        entries={topStreaks}
        valueKey="streak"
        formatValue={(value) => `${value} days`}
        showStreakIcon
      />

      {perfectQuarters.length > 0 && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
            Perfect quarters 🎯
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {perfectQuarters.map((entry) => entry.user?.displayName || 'Member').join(', ')}
            {' '}
            hit 100% habit consistency
          </Typography>
        </>
      )}

      {personalBests.length > 0 && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
            Personal bests
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
            {personalBests.map((entry) => (
              <Typography key={entry.userId} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {entry.user?.displayName || 'Member'}
                </Box>
                {' '}
                set a new personal best streak of {entry.streak} days
              </Typography>
            ))}
          </Box>
        </>
      )}

      {booksFinished.total > 0 && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
            Books finished
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <MenuBookIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {booksFinished.total} book{booksFinished.total === 1 ? '' : 's'} finished this quarter
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
            {(booksFinished.items || []).map((entry) => (
              <Typography key={`${entry.userId}-${entry.bookId}`} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {entry.user?.displayName || 'A reader'}
                </Box>
                {' '}
                finished {entry.title}
                {entry.author ? ` by ${entry.author}` : ''}
              </Typography>
            ))}
          </Box>
        </>
      )}

      {(topPerformers.mostConsistent ||
        topPerformers.streakChampion ||
        topPerformers.topMetricEarner ||
        topPerformers.milestoneMaster) && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
            Highlights
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
            <TopPerformerLine
              label="Most consistent"
              performer={topPerformers.mostConsistent}
              formatValue={formatConsistency}
            />
            <TopPerformerLine
              label="Longest daily streak"
              performer={topPerformers.streakChampion}
              formatValue={(value) => `${value} days`}
            />
            <TopPerformerLine
              label="Top metric progress"
              performer={topPerformers.topMetricEarner}
              formatValue={formatConsistency}
            />
            <TopPerformerLine
              label="Milestones completed"
              performer={topPerformers.milestoneMaster}
              formatValue={(value) => `${value}`}
            />
          </Box>
        </>
      )}

      <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
        <Link
          component={RouterLink}
          to={buildQuarterGoalsReportPath(payload)}
          underline="hover"
          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
        >
          See more!
        </Link>
      </Box>
    </Box>
  );
};

export default QuarterGoalsReportActivity;
